import {
  AccountLookupResponse,
  QuickscreenOfferResponse,
} from '@weave/schema-gen-ts/dist/schemas/payments-platform/care-credit-integration/care_credit_integration.pb';
import { i18next } from '@frontend/i18n';
import { CareCreditPatientStatus } from '../../../types/care-credit.types';
import { EnrollmentFormFieldNames } from '../enrollment-multi-step.types';

const CARE_CREDIT_PATIENT_STATUSES: readonly CareCreditPatientStatus[] = [
  'CARDHOLDER',
  'PREAPPROVED',
  'PREQUALIFICATION_NEEDED',
  'UNKNOWN',
];

export const quickOfferResponseIsSuccess = (response: QuickscreenOfferResponse): boolean => {
  if (quickOfferResponseIsDeclined(response)) {
    return false;
  }

  if (response.errorResponse) {
    return false;
  }

  return true;
};

export const quickOfferResponseIsDeclined = (response: QuickscreenOfferResponse): boolean => {
  const decisionMessage = response.decisionMessage?.toLocaleLowerCase() || '';
  // DECLINED, DECLINED NO HIT, THROTTLE DECLINED

  if (response.decisionCode === '0000' || decisionMessage.includes('declined')) {
    return true;
  }

  return false;
};

export type QuickOfferFieldError = {
  field: EnrollmentFormFieldNames;
  message: string;
};

export const getQuickOfferResponseErrorDetails = (response: QuickscreenOfferResponse) => {
  if (!response.errorResponse) {
    return null;
  }

  const { errorCode, errorMessage, errors } = response.errorResponse;

  const map = {
    'applicantInfo.dateOfBirth': 'dateOfBirth',
    'applicantInfo.annualIncome': 'monthlyNetIncome',
    'applicantInfo.email': 'email',
    'applicantInfo.firstName': 'firstName',
    'applicantInfo.lastName': 'lastName',
    'applicantInfo.contact.contactNumber': 'phoneNumber',
    'applicantInfo.contact.contactType': 'phoneNumberType',
    'applicantInfo.address.addressLine1': 'address',
    'applicantInfo.address.addressLine2': 'address2',
    'applicantInfo.address.city': 'city',
    'applicantInfo.address.state': 'state',
    'applicantInfo.address.zipCode': 'postalCode',
  };

  let genericMessage = '';
  const fieldErrors = errors?.reduce((acc, e) => {
    const { field, reason } = e;
    if (!field) {
      genericMessage += '; ' + reason;
      return acc;
    }
    const fieldName = field.replace(/^\./, '');
    const segments = fieldName.split('.');

    // not nested. So get last segment.

    let mapKey = '';

    const firstSegment = segments.shift();
    if (firstSegment !== 'applicantInfo') {
      console.warn('Got unexpected error field format', field);
      genericMessage += '; ' + reason;
      return acc;
    }

    mapKey = `${firstSegment}`;

    let nextSegment = segments.shift();
    if (nextSegment === 'cypher') {
      nextSegment = segments.shift();
    }

    if (nextSegment === 'contact' || nextSegment === 'address') {
      // skip index segment
      mapKey += `.${nextSegment}`;
      segments.shift();
    }

    mapKey += `.${segments.join('.')}`;

    if (!Object.prototype.hasOwnProperty.call(map, mapKey)) {
      console.warn('Got unexpected error fieldKey: ', mapKey, 'in field', field);
      genericMessage += '; ' + reason;
      return acc;
    }

    acc.push({
      field: map[mapKey as keyof typeof map] as EnrollmentFormFieldNames,
      message: reason || i18next.t('Unknown or invalid value', { ns: 'payments-care-credit' }),
    });
    return acc;
  }, [] as QuickOfferFieldError[]);

  return {
    errorCode,
    errorMessage: errorMessage + genericMessage,
    fieldErrors: fieldErrors || [],
  };
};

export const getQuickOfferStatus = (
  response: QuickscreenOfferResponse,
  accountLookupResponse?: AccountLookupResponse
): CareCreditPatientStatus | undefined => {
  // unprocessableEntity represents a special case where Synchrony's account
  // lookup endpoint knows that a patient closed their account, but Synchrony's
  // quickscreen endpoint will not know. in the event that the account lookup
  // response returns this error code, it is expected behavior to disregard
  // the status value returned in `response`, and return early. returning the
  // PREQUALIFICATION_NEEDED will prompt the user to sign up for a Synchrony
  // account again.
  if (accountLookupResponse?.errorResponse?.errorCode === 'credit.accountlookup.unprocessableEntity') {
    return 'PREQUALIFICATION_NEEDED';
  }
  return CARE_CREDIT_PATIENT_STATUSES.find((allowedStatus) => allowedStatus === response.weaveStatus);
};
